import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { useDynamicReportApi } from '../../../services/api/dynamicReportApi/useDynamicReportApi';
import { COMPONENT_STATUS } from '../../../services/constants';
import { setReportDataStatus } from '../../campaigns/modules/campaigns-reports/actions';
import {
    METAMODEL_NODE_TYPE,
    transformMetaModelNodesToList,
} from '../../campaigns/modules/campaigns-reports/services/dynamicReportsService';
import { addIndication, INDICATION_TYPES } from '../../taboola-common-frontend-modules/Indications';
import { FEATURE_FLAGS, useAccount } from '../../taboola-common-frontend-modules/account-configurations';
import useAccountConfig from '../../taboola-common-frontend-modules/account-configurations/hooks/useAccountConfig';
import { usePermissions } from '../../taboola-common-frontend-modules/authentication';
import { FormattedMessage } from '../../taboola-common-frontend-modules/i18n';

const RETRY_ATTEMPTS = 2;
const HOUR = 60 * 60 * 1000;

export const META_MODEL_TYPE_PERFORMANCE = 'PERFORMANCE';
export const DYNAMIC_REPORT_META_MODEL_QUERY_PREFIX = 'DYNAMIC_REPORT_META_MODEL_QUERY_PREFIX';

const getErrorIndication = messageId => ({
    message: <FormattedMessage id={messageId} />,
    type: INDICATION_TYPES.ERROR,
    highlight: <FormattedMessage id="error.highlight" />,
});

export const useDynamicReportMetaModel = metaModelType => {
    const { accountName } = useAccount();
    const dispatch = useDispatch();
    const dynamicReportApi = useDynamicReportApi();
    const isPermitted = usePermissions('PERFORMANCE_DYNAMIC_REPORT_VIEW');
    const enabled = !!metaModelType && isPermitted && !!accountName;
    const { [FEATURE_FLAGS.DYNAMIC_REPORTS_METAMODEL_EXCLUDE_NODE_LIST]: excludeNodes } = useAccountConfig([
        FEATURE_FLAGS.DYNAMIC_REPORTS_METAMODEL_EXCLUDE_NODE_LIST,
    ]);
    const excludeNodeList = useMemo(() => {
        return excludeNodes.trim().split(',');
    }, [excludeNodes]);

    const { data, isSuccess } = useQuery(
        [DYNAMIC_REPORT_META_MODEL_QUERY_PREFIX, accountName],
        async () => {
            const result = await dynamicReportApi.getMetaModel(accountName, metaModelType);
            const nodeValues = result?.report?.nodes?.values || [];

            return {
                ...result,
                dimensions: transformMetaModelNodesToList(nodeValues, METAMODEL_NODE_TYPE.ROW, excludeNodeList),
                metrics: transformMetaModelNodesToList(nodeValues, METAMODEL_NODE_TYPE.COLUMN, excludeNodeList),
            };
        },
        {
            onError: () => {
                dispatch(setReportDataStatus(COMPONENT_STATUS.ERROR));
                dispatch(addIndication(getErrorIndication('dynamic.reports.dynamic.report.metamodel.fetch.failure')));
            },
            enabled,
            retry: RETRY_ATTEMPTS,
            cacheTime: HOUR,
            staleTime: HOUR,
        }
    );

    return {
        isSuccess: isSuccess && enabled,
        metaModel: data,
    };
};
